/**
 * Dirty-flag render gate for the panels editor Pixi stage.
 *
 * The animation loop still runs at rAF so resize / initial zoom / bg-load
 * bookkeeping stay responsive, but `renderer.render(stage)` is skipped
 * unless something actually changed the scene (panels, cursor, pan, zoom,
 * rubber band, etc.). Idle frames with large arrays are the main cost.
 */

let stageDirty = true;

export function markStageDirty() {
  stageDirty = true;
}

export function isStageDirty() {
  return stageDirty;
}

export function consumeStageDirty() {
  if (!stageDirty) {
    return false;
  }
  stageDirty = false;
  return true;
}

export function resetStageDirty(dirty = false) {
  stageDirty = dirty;
}

type RenderableRenderer = {
  render: (stage: unknown) => void;
};

type RenderableStage<TChildren = unknown[]> = {
  children: TChildren;
};

export function shouldRenderPanelsEditorFrame(force?: boolean) {
  const dirty = consumeStageDirty();
  return !!force || dirty;
}

export function renderPanelsEditorFrame<TChildren>(
  renderer: RenderableRenderer | null | undefined,
  stage: RenderableStage<TChildren> | null | undefined,
  options: {
    force?: boolean;
    beforeRender?: () => void;
    sortChildren?: (children: TChildren) => TChildren;
  } = {},
) {
  if (!renderer || !stage) {
    return false;
  }

  if (!shouldRenderPanelsEditorFrame(options.force)) {
    return false;
  }

  if (options.beforeRender) {
    options.beforeRender();
  }

  if (options.sortChildren) {
    stage.children = options.sortChildren(stage.children);
  }

  renderer.render(stage);
  return true;
}
